import * as Sentry from '@sentry/react';
import type { History } from 'history';
import { isStandaloneMode } from '@constants/app-modes';
import { sentryBeforeSend } from '@helpers/sentryHelpers';

declare const IS_DEV: boolean;
declare const SENTRY_DSN: string;
declare const GIT_SHA: string;
declare const REALM: string;

let initialized = false;

/**
 * Single Sentry.init used by all three realm route files (DIY, Concierge,
 * Publisher). Adds what the per-file copies were missing:
 *  - `environment`, derived from the hostname, so dev noise is separable;
 *  - `release`, pinned to the build's git SHA, so regressions map to deploys;
 *  - a `realm` tag to tell the three apps apart in one Sentry project.
 */
const initSentry = (history: History): void => {
  if (initialized || !isStandaloneMode() || IS_DEV || !SENTRY_DSN) {
    return;
  }
  initialized = true;

  const { hostname } = window.location;
  const environment =
    hostname === 'dev.respona.com' || hostname.startsWith('dev-') ? 'development' : 'production';

  Sentry.init({
    dsn: SENTRY_DSN,
    environment,
    release: typeof GIT_SHA === 'string' && GIT_SHA ? GIT_SHA : undefined,
    beforeSend: sentryBeforeSend,
    integrations: [
      Sentry.reactRouterV5BrowserTracingIntegration({
        history,
      }),
      // Replays capture the DOM. Inbox / CRM screens are full of correspondent
      // PII (email bodies, names, addresses), so everything is masked at capture
      // time; these are the SDK defaults but are pinned here so a future SDK
      // default change cannot silently start recording clear text.
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        // Never attach request / response bodies or headers to replays.
        networkDetailAllowUrls: [],
        networkCaptureBodies: false,
      }),
    ],
    // Scrub raw HTTP payloads from breadcrumbs / events as well.
    sendDefaultPii: false,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // ignoreErrors: ['ResizeObserver loop limit exceeded'],
    // tracesSampleRate: 1.0,
  });

  Sentry.setTag('realm', typeof REALM === 'string' ? REALM : 'unknown');
};

export default initSentry;
